import {
	all,
	Checkbox,
	Div,
	H2,
	Button,
	Label,
	NumberInput,
	ElementRenderer,
	Span,
	H4,
	P,
	VNumber,
	reactive,
} from 'alkali'
import { analysisSelections, questionAsParameters, structuredQuery, currentAnalysisSelections, faqOrReadOnly } from 'model/SearchQuery'
import { get } from 'tools/request'
import { selectedOutcomes } from 'model/EvidenceSearch'
import {
	numToString,
	outcomeMeasureName,
	identifyGroups,
	computeComparativeEffect,
	getCIWidth,
} from 'model/AnalyzeStudy'
import Dialog from '../generic/DialogAlkali'
import { selectedStudyData } from 'model/StudyCollection'
import * as Loading from 'images/loading-clock.svg'
import Icon from 'components/Icon'
import './RegressionDialog.scss'
import * as warnIcon from 'icons/alert-triangle.svg'
import * as passIcon from 'icons/data-file-check.svg'
import { Toast, displayHiddenToast, hideToast } from 'components/generic/Toast'
import { track } from 'components/tracking/track'

let loadedAnalysis = reactive(false);

const loadingClock = new Div('.clock-container', {}, [
	new Icon('.clock-img.clock-minutes.clock-hours', {
		icon: Loading,
	}),
	new P('.animated')
])

const directComparison = structuredQuery.interventions.to(interventions =>
	currentAnalysisSelections.analysisType.to(analysisType => analysisType == 'direct' || analysisType == 'cohort' || analysisType == 'auto' && interventions.length <= 1))
// 
const needRegressionData = (enoughData) => {
	let updatedView = enoughData ? 'ver' : 'hor'
	const parentElement = document.querySelector('.analysis-content')
	parentElement.setAttribute('dir', updatedView)
	const regressionElement = document.querySelector('.regression-container')
	regressionElement.remove()
}

export function showRegressionForCovariate({
	name,
	valueFromGroup,
	selected,
	onSave,
	characteristicSetting,
	characteristicAnalysis
}) {
	let regressionDialog = new RegressionDialog({
		head: {
			titleText: `${name} Analysis & Settings`,
		}
	}) // TODO: Reuse same dialog instance?
	let studyData = selectedStudyData.valueOf()
	let correlationData = studyData.map((studyRow) => {
		let groups = identifyGroups(studyRow.study, studyRow.groups)
		let { effect, isComparative } = computeComparativeEffect(studyRow.row, groups)
		//if (!isComparative)
			//return
		let N = 0
		let total = 0
		for (let group of groups) {
			if (group.isComparing) {
				let covariateValue = valueFromGroup(group)
				if (group.N && covariateValue != undefined) {
					N += group.N
					total += covariateValue * group.N
				}
			}
		}
		let meanCovariate = total / N
		return {
			x: meanCovariate,
			y: effect,
			N,
			name: studyRow.study.name,
			id: studyRow.study.id,
		}
	}).filter(study => study)
	correlationData.xName = name
	regressionDialog.data = correlationData
	regressionDialog.selected = selected
	regressionDialog.characteristicSetting = characteristicSetting
	regressionDialog.characteristicAnalysis = characteristicAnalysis
	regressionDialog.onSave = onSave
	regressionDialog.show()
	track({ event: name + '-dialog' })
}

const regressionAnalysis = (covariate, data, useRegularization, treatmentVariance) => {
	let analyzableData = []

	let ciWidth = getCIWidth()

	let outcomeLabel = selectedOutcomes.to((outcomes) =>
		outcomes[0]
			? outcomes[0].label + ' ' + outcomeMeasureName(outcomes[0])
			: 'No outcome selected'
	)
	let outcome = selectedOutcomes.get(0)

	var margin = { top: 10, right: 30, bottom: 30, left: 60 }
	// append the svg object to the body of the pages
	let isRatio

	let settings = analysisSelections.settings.valueOf()

	if (outcome?.type == 'Binary') {
		let measureBin = settings?.measureBin || 'RR'
		isRatio = measureBin != 'RD'
	}

	let yMin = Infinity,
		yMax = -Infinity,
		xMin = Infinity,
		xMax = -Infinity

	for (let point of data) {
		if (point.y > yMax) yMax = point.y
		if (point.y < yMin) yMin = point.y
		if (point.x > xMax) xMax = point.x
		if (point.x < xMin) xMin = point.x
		if (isRatio) {
			point = Object.assign({}, point, {
				y: Math.log(point.y),
			})
		}
		analyzableData.push(point)
	}

	const getData = async () => {
		try {
			const parameters = await questionAsParameters()
			const result = await get(
				'/portal/analysis/meta?' +
				parameters +
				'&regression=' +
				encodeURIComponent(covariate)
			)
			if (result) {
				let intervention = result?.interventions.find(
					(intervention) => intervention.regressionResults
				)
				let regressionResults = intervention?.regressionResults
				if (regressionResults) {
					Object.assign(regressionResults, intervention)
					regressionResults.studyCount = result.studyCount
					loadedAnalysis.put(true)
				}
				return regressionResults
			} else {
				return false
			}
		} catch (err) {
			return false
		}
	}

	let analysisRequest = reactive(
		analyzableData.length > 1 && directComparison ? getData().then(
			(regressionDiagnostics) => {
				if (regressionDiagnostics) {
					let { m, bForM, xMean, residualForM, y2Mean } = calculateRegression(analyzableData)

					let covariateLabel =
						covariate == 'gender' ? 'Gender (male)' : covariate[0].toUpperCase() + covariate.slice(1)

					// Add X axis
					let xRange = xMax - xMin,
						yRange = yMax - yMin,
						xMinValues = (xMin = xMin - xRange / 10),
						xMaxValues = (xMax = xMax + xRange / 10),
						xMinVar = reactive(+numToString(xMin)),
						xMaxVar = reactive(+numToString(xMax))

					let regularizedRegression = all(
						[treatmentVariance, analysisRequest],
						(treatmentVariance, regression) => {
							if (!regression) return null
							let {
								mod,
								name,
								se,
								effect,
								'ci.lb': lb,
								'ci.ub': ub,
								pval,
								lowerBound,
								upperBound,
							} = regressionDiagnostics
							let covariatePriorSE =
								(Math.sqrt(y2Mean) * Math.sqrt(treatmentVariance / 100)) / // its a percentage
								(covariate == 'gender' ? 100 : 50) // estimated range of covariate)
							let adjustedM =
								mod / (se * se) / (1 / (se * se) + 1 / (covariatePriorSE * covariatePriorSE))
							let adjustedSE = Math.sqrt(
								1 / (1 / (se * se) + 1 / (covariatePriorSE * covariatePriorSE))
							)
							let z = Math.abs(adjustedM / adjustedSE)
							return {
								m: adjustedM,
								se: adjustedSE,
								name,
								lb: adjustedM - adjustedSE * ciWidth,
								ub: adjustedM + adjustedSE * ciWidth,
								pval: Math.exp(-0.717 * z - 0.416 * z * z),
							}
						}
					)

					// From screening merge
					let manualSlope = new VNumber()

					let slope = all(
						[useRegularization, regressionDiagnostics, regularizedRegression],
						(useRegularization, regression, regularizedRegression) => {
							if (!regression) return null
							manualSlope.put(null)
							if (useRegularization) {
								return regularizedRegression
							} else {
								let {
									mod,
									name,
									se,
									effect,
									'ci.lb': lb,
									'ci.ub': ub,
									pval,
									lowerBound,
									upperBound,
								} = regression
								return {
									m: mod,
									lb,
									ub,
									se,
									pval,
								}
							}
						}
					)

					// From screening merge
					let regression = all([slope, manualSlope], (regression, manualSlope) => {
						if (manualSlope != null) {
							let { se } = regression
							return Object.assign({}, regression, {
								m: manualSlope,
								lb: manualSlope - ciWidth * se,
								ub: manualSlope + ciWidth * se,
							})
						}
						return regression
					})
					let rSlope = regression.to(
						({ m }) => +numToString(m),
						(m) => manualSlope.put(m)
					)
					//
					let { mod, name, effect, pval } = regressionDiagnostics
					let magnifies = mod > 0 == effect > 0

					let statement = (name +
						(pval < 0.1 ? ' probably ' : ' possibly ') +
						(effect > 0 ? 'increases ' : 'decreases ') +
						(outcome.type == 'Binary' ? 'the chances of ' : '') +
						selectedOutcomes.valueOf()[0]?.label +
						' more for ' +
						(covariate == 'gender'
							? magnifies
								? 'male'
								: 'female'
							: covariate == 'age'
								? magnifies
									? 'older'
									: 'younger'
								: magnifies
									? 'with ' + covariate
									: 'without ' + covariate) +
						' patients.'
					)
					const regressionData = {
						statement,
						slope,
						covariateLabel,
						outcomeLabel,
						xMin,
						xMax,
						xMinValues,
						xMaxValues,
						xMinVar,
						xMaxVar,
						yMin,
						yMax,
						xRange,
						yRange,
						analyzableData: [],
						m,
						bForM,
						xMean,
						residualForM,
						y2Mean,
						isRatio,
						regressionDiagnostics,
						regularizedRegression,
						margin,
						rSlope,
						regression,
						outcome,
					};
					return regressionData
				} else {
					return false
				}
			},
			(error) => {
				loadingClock.remove()
				const regressionElement = document.querySelector('.regression-container')
				regressionElement.classList.remove('loading')
				loadedAnalysis.put(true)
				console.error
				return false
			}
		).catch(err => {
			return false
		}) : Promise.resolve(false)
	)
	return analysisRequest

}

const RegressionCheck = (toastId) => Div('.regression-check', {
	onmouseover() {
		displayHiddenToast(toastId)
	},
	onmouseleave() {
		hideToast(toastId)
	},
})

const RegressionToast = (toastId, toastTitle, toastMessage) => Toast(`.right.hover-toast#${toastId}`, {
	id: toastId,
	hover: true,
	child: true,
	title: toastTitle,
	message: toastMessage,
	hoverClose: true
})

const Significant = RegressionCheck('significant')
const SignificantToast =
	RegressionToast(
		'significant',
		'Significance (P-Value)',
		'The p-value indicates the probability that this correlation or stronger was observed without any true relationship (null hypothesis)',
	)

const I2 = RegressionCheck('i2')
const I2Toast =
	RegressionToast(
		'i2',
		'Heterogeneity (I²)',
		'This indicates the amount of heterogeneity (I²) that can be explained by this variable. However, it is frequently the case that many other factors may influence outcomes, so one should not necessarily expect all heterogeneity to be explained here.',
	)

const StudyCount = RegressionCheck('study-count')
const StudyCountToast =
	RegressionToast('study-count',
		'Study Count',
		'More studies provide more robust regression analysis, and it may be advisable to search for an entire drug class and use broad conditions or outcomes (or no conditions) to provide sufficient data.',
	)

const Regularization = RegressionCheck('regularization')
const RegularizationToast = (covariate) =>
	RegressionToast(
		'regularization',
		'Regularization',
		'Regularization priors uses internally analyzed/machine-learned distribution of typical treatment effects related to ' +
		covariate +
		'to estimate the probable distribution of effects, which constrains weak inferences that may suggest unlikely interpolations of data over the range of the variable (over-fitting), and provides the most likely effect. This is recommended for estimating effects for the purpose of adjusting for covariate effect, as it yields more robust and realistic analysis.',
	)

const RegPrior = RegressionCheck('reg-prior')
const RegPriorToast =
	RegressionToast(
		'reg-prior',
		'Regularization Priors',
		'Enabling this will apply a regularization prior of a typical range of moderator effects on the treatment, using the provided expected standard deviations in moderator effects, to provide the most likely causative moderator effect. This is more robust against over-fitting and spurious correlations.'
	)

function calculateRegression(data) {
	let xSum = 0
	let ySum = 0
	let x2Sum = 0
	let xySum = 0
	let y2Sum = 0
	let N = 0
	for (let point of data) {
		xSum += point.x * point.N
		ySum += point.y * point.N
		x2Sum += point.x * point.x * point.N
		y2Sum += point.y * point.y * point.N
		xySum += point.x * point.y * point.N
		N += point.N
	}
	let m = (N * xySum - xSum * ySum) / (N * x2Sum - xSum * xSum)
	let bForM = (m) => (ySum - m * xSum) / N
	let residualForM = (m) => {
		let yMean = ySum / N
		let b = bForM(m)
		let sumWith = 0
		let sumWithout = 0
		for (let point of data) {
			sumWithout += (yMean - point.y) * (yMean - point.y) * point.N
			let diff = point.y - (point.x * m + b)
			sumWith += diff * diff * point.N
		}
		return (sumWithout - sumWith) / sumWithout
	}
	let xMean = xSum / N
	let y2Mean = y2Sum / N
	return { m, bForM, xMean, residualForM, y2Mean }
}
export class RegressionDialog extends Dialog('.regression-dialog', {
    classes: {
                'readonly-mode': faqOrReadOnly,
            },
    }) {
	// types for RegressionDialog to avoid type errors
	characteristicSetting: alkali.ElementClass<HTMLElement>;
	characteristicAnalysis: alkali.ElementClass<HTMLElement>;
	name: string;
	data: any;

	get defaults() {
		return {
			id: 'regression-dialog',
		}
	}

	show() {
		// Show regression modal
		super.show()
		//If regression analysis is available
		if (directComparison.valueOf()) {
			const studyData = selectedStudyData.valueOf().length
			// Can regress but no selected studies
			if (!studyData) {
				let raceDialog = this.data?.xName == 'race'
				this.body.append(
					Div(`.regression-container.alternate-view${raceDialog ? '.race-content' : ''}`, [
						Div('.regression-dialog-left-pane', [
							// Until we support regression for race, we want to remove regression content
							!raceDialog ? (
							Div('.regression-summary', [
								H2('.strong-label', ['Regression Analysis']),
								P('.strong', ['Please select more studies with age data to run a regression analysis'])
							])) : null,
							Div('.characteristic-analysis', [
								H2('.strong-label', ['Population Analysis']),
								...(this.characteristicAnalysis || []),
							])
						]),
						Div('.regression-dialog-right-pane', [
							Div('.settings-container', [
								H2('.strong-label', ['Settings']),
								...(this.characteristicSetting || []),
							]),
							Div('.save', [
								Button('.primary', {
									content: 'Save',
									onclick: () => save(),
								}),
							]),
						])
					]),
				)
				// Can regress and selected studies
			} else {
				// Type of regression
				let data = this.data.filter(({ x, y, N }) => !isNaN(x) && !isNaN(y) && !isNaN(N))
				let covariate = this.data.xName
				// capitalizedCovariate = covariate.charAt(0).toUpperCase() + covariate.slice(1)
				let isBinaryCovariate = covariate == 'gender'
				let useRegularization = reactive().to(
					() => localStorage.getItem('use-regularization') != null,
					(useRegularization) =>
						localStorage[useRegularization ? 'setItem' : 'removeItem']('use-regularization', true)
				)
				const treatmentVarianceStored = localStorage.getItem('treatment-variance') ? parseInt(localStorage.getItem('treatment-variance')): null
				let treatmentVarianceValue = treatmentVarianceStored ? treatmentVarianceStored : 95
				let treatmentVariance = reactive(numToString(treatmentVarianceValue))
					this.body.append(
					loadingClock,
				)
				regressionAnalysis(covariate, data, useRegularization, treatmentVariance).then((regressionObject) => {
					if (!regressionObject.valueOf()) {
						loadingClock.remove()
						let raceDialog = this.data?.xName == 'race'
						this.body.append(
							Div(`.regression-container.alternate-view${raceDialog ? '.race-content' : ''}`, [
								Div('.regression-dialog-left-pane', [
								// Until we support regression for race, we want to remove regression content
								!raceDialog ? (
									Div('.regression-summary', [
										H2('.strong-label', ['Regression Analysis is not applicable']),
										P(['Your currently do not have enough direct comparison data to run a regression analysis']),
										P([Span('.strong', ['Note: ']), `Try selecting more studies from 'Search Results' that have ${covariate} data.`])
									])) : null,
									Div('.characteristic-analysis', [
										H2('.strong-label', ['Population Analysis']),
										...(this.characteristicAnalysis || []),
									])
								]),
								Div('.regression-dialog-right-pane', [
									Div('.settings-container', [
										H2('.strong-label', ['Settings']),
										...(this.characteristicSetting || []),
									]),
									Div('.save', [
										Button('.primary', {
											content: 'Save',
											onclick: () => save(),
										}),
									]),
								])
							])
						)
					} else {
						let { statement,
							slope,
							covariateLabel,
							outcomeLabel,
							xMin,
							xMax,
							xMinValues,
							xMaxValues,
							xMinVar,
							xMaxVar,
							yMin,
							yMax,
							xRange,
							yRange,
							analyzableData: [],
							m,
							bForM,
							xMean,
							residualForM,
							y2Mean,
							isRatio,
							regressionDiagnostics,
							regularizedRegression,
							margin,
							rSlope,
							regression,
							outcome } = regressionObject.valueOf()
						let currentQuestion = document.querySelector('.main-search').value
						let liveCovariate = new VNumber(null),
							liveEffect = new VNumber(null)
						let regressionEnabled = reactive(analysisSelections.regression.valueOf() == covariate)
						let controlEnabled = reactive(
							analysisSelections.adjustments.valueOf()?.some(({ predictor }) => predictor == covariate))
						let chartDiv = new Div('.regression-chart')
						this.body.append(
							Div('.regression-container.analysis-content.loading', {
								attributes: { dir: 'hor' },
								content: [
									Div('.regression-dialog-left-pane', [
										Div('.regression-summary', [
											Div('.regression-top', [
												H2('.strong-label', ['Regression Analysis']),
												Div('.regression-statement', [
													Span('.italic', [Span('.linear-text', ['Evidently, ']), statement]),
													Div('.moderator', [
														P('.strong-label', ['Moderator']),
														Span('.regular', [
															slope.to((slope) => {
																if (slope) {
																	let { m, lb, ub } = slope
																	return numToString(m) + ' (' + numToString(lb) + ' to ' + numToString(ub) + ')'
																} else {
																	return 'false'
																}
															}).whileResolving(Span('.linear-text.loading', ['Analyzing studies..']))
														]),
													]),
												]),
												Div('.chart-container', [
													H2('.regression-independent-label', [outcomeLabel]),
													chartDiv,
												]),
												Div('.regression-dependent-label', [covariateLabel]),
											]),
											Div('.controls', [
												Div('.settings-header', [H2('.strong-label', ['Graph Settings'])]),
												Div('.regression-controls', [
													Div('.graph-controls', [
														Div('.regression-range', [
															Div([
																Label('.min', [
																	'Min: ',
																	NumberInput({
																		valueAsNumber: xMinVar,
																		step: 0,
																	}),
																]),
															]),
															Div([
																Label('.max', [
																	'Max: ',
																	NumberInput({
																		valueAsNumber: xMaxVar,
																		step: 0,
																	}),
																]),
															]),
														]),
														Div([
															Div('.range-header', [
																Span('.clickable', {
																	content: '↔',
																	title: 'Show full range of values',
																	onclick() {
																		xMinVar.put(0)
																		xMaxVar.put(covariate == 'age' ? 80 : 100)
																	},
																}),
																H4(['Show full range of values']),
															]),
															Div('.range-header', [
																Span('.clickable', {
																	content: '⇲',
																	title: 'Show range of values from studies',
																	onclick() {
																		xMinVar.put(xMinValues)
																		xMaxVar.put(xMaxValues)
																	},
																}),
																H4(['Show range of values from studies']),
															]),
														]),
													]),
													Div('.adjusted-effect', [
														Div('.strong-label', [H4(['Adjusted Effect'])]),
														// To Do: Add load state to adjusted values as it display some time after page has set up
														isBinaryCovariate
															? Div(
																'.adjusted-values',
																slope.to((slope) => {
																	if (slope) {
																		let { m } = slope,
																			b = bForM(m)
																		return [
																			Div('.adjusted-0', [
																				'Male: ' +
																				numToString(isRatio ? Math.exp(b + 100 * m) : b + 100 * m),
																			]),
																			Div('.adjusted-100', [
																				'Female: ' + numToString(isRatio ? Math.exp(b) : b),
																			]),
																		]
																	} else {
																		return ['false']
																	}
																}).whileResolving(Span('.linear-text.loading', ['Loading..']))
															)
															: null,
														Div('.adjusted-values', [
															Div('.live-covariate', [
																liveCovariate.to((v) =>
																	v != null ? covariateLabel + ': ' + numToString(v) : ''
																),
															]),
															Div('.live-effect', [
																liveEffect.to((v) => (v != null ? 'Effect: ' + numToString(v) : '')),
															]),
														]),
													]),
												]),
											]),
										]),
									]),
									Div('.regression-dialog-right-pane', [
										Div('.right-analysis', [
											Div('.regression-checklist', [
												all(
													[regressionDiagnostics, regularizedRegression, useRegularization],
													(regression, regularizedRegression, usingRegularation) => {
														if (!regression) return
														let i2Ratio = regression.modI2 / regression.I2
														let regularizatioRatio = regularizedRegression.m / regression.mod
														let pval = usingRegularation ? regularizedRegression.pval : regression.pval
														return [
															H2('.strong-label', ['Insights']),
															pval < 0.05
																?
																Significant('.regression-passes', [
																	Icon({ icon: passIcon }),
																	'The correlation is statistically significant, P = ' + numToString(pval),
																	SignificantToast
																])
																: pval < 0.15
																	? Significant('.regression-warning', [
																		Icon({ icon: warnIcon }),
																		'The correlation is of statistical interest, P = ' + numToString(pval),
																		SignificantToast
																	])
																	: Significant(
																		usingRegularation ? '.regression-warning' : '.regression-failed',
																		[
																			Icon({ icon: warnIcon }),
																			'The correlation is not statistically significant, P = ' +
																			numToString(pval),
																			SignificantToast
																		]
																	),
															i2Ratio < 0.5
																? I2('.regression-passes', [
																	Icon({ icon: passIcon }),
																	`The majority of heterogeneity (I² = ${Math.round(
																		regression.I2
																	)}%) can be explained by ${covariate} (I² = ${Math.round(
																		regression.modI2
																	)}%)`,
																	I2Toast
																])
																: i2Ratio < 0.85
																	? I2('.regression-warning', [
																		Icon({ icon: warnIcon }),
																		`Some heterogeneity (I² = ${Math.round(
																			regression.I2
																		)}%) can be explained by ${covariate} (I² = ${Math.round(
																			regression.modI2
																		)}%)`,
																		I2Toast
																	])
																	: I2(usingRegularation ? '.regression-warning' : '.regression-failed', [
																		Icon({ icon: warnIcon }),
																		`Very little heterogeneity (I² = ${Math.round(
																			regression.I2
																		)}%) can be explained by ${covariate} (I² = ${Math.round(
																			regression.modI2
																		)}%)`,
																		I2Toast
																	]),
															regression.studyCount < 10
																? StudyCount('.regression-warning', [
																	Icon({ icon: warnIcon }),
																	'Only ' +
																	regression.studyCount +
																	' studies selected, more studies can improve results',
																	StudyCountToast
																])
																: StudyCount('.regression-passes', [
																	Icon({ icon: passIcon }),
																	regression.studyCount + ' studies selected',
																	StudyCountToast
																]),
															usingRegularation
																? Regularization('.regression-passes', [
																	Icon({ icon: passIcon }),
																	'Regularization prior applied, most likely effect for covariate used',
																	RegularizationToast(covariate)
																])
																: regularizatioRatio < 0.5
																	? Regularization('.regression-failed', [
																		Icon({ icon: warnIcon }),
																		'Over-fitting is very likely, and regularization is recommended',
																		RegularizationToast(covariate)
																	])
																	: regularizatioRatio < 0.8
																		? Regularization('.regression-warning', [
																			Icon({ icon: warnIcon }),
																			'Over-fitting is somewhat likely, and regularization is recommended',
																			RegularizationToast(covariate)
																		])
																		: Regularization('.regression-passes', [
																			Icon({ icon: passIcon }),
																			'Over-fitting is minimal (regularization can still be applied)',
																			RegularizationToast(covariate)
																		]),
														]
													}
												)
											]),
											Div('.regression-prior', [
												RegPrior([
													Label([
														Checkbox('.checkbox-label', { checked: useRegularization }),
														'Apply regularization priors',
														RegPriorToast,
														Label('.flex-label',
															{
																display: useRegularization,
															},
															[
																Div('.prior-content', [
																Span('.helper-text', [`Expected treatment effect variance across range of ${covariate}: `]),
																	NumberInput('#treatmentVariance', {
																		valueAsNumber: treatmentVariance,
																		step: 1,
																		min: 0,
																		onclick: (event) => {
																			event.stopPropagation()
																		}
																	}),
																	'%',
																])
															]
														),
													])
												])
											]),
											Div('.characteristic-analysis', [
												H2('.strong-label', ['Population Analysis']),
												...(this.characteristicAnalysis || []),
											]),
											Div('.settings-container', [
												H2('.strong-label', ['Settings']),
												...(this.characteristicSetting || []),
												Div('.regression-setting', [
													Label('.checkbox-label', [
														Checkbox({
															checked: regressionEnabled,
														}),
														'Enable regression analysis of ' + covariate + ' in report',
													]),
												]),
												Div('.regression-setting', [
													Label([
														Checkbox({
															checked: controlEnabled,
														}),
														'Control for ' + covariate,
													]),
												]),
											]),
										]),
										Div('.save', [
											Button('.primary', {
												content: 'Save',
												onclick: () => {
													if (regressionEnabled.valueOf())
														analysisSelections.regression.put(covariate)
													else if (analysisSelections.regression == covariate)
														analysisSelections.regression.put(null)
													let adjustments = analysisSelections.adjustments.valueOf()
													let existingAdjustment = adjustments?.find(
														({ predictor }) => predictor == covariate
													)
													if (existingAdjustment) {
														analysisSelections.adjustments.splice(
															adjustments.indexOf(existingAdjustment),
															1
														)
													} if (controlEnabled.valueOf()) {
														analysisSelections.adjustments.push({
															m: rSlope.valueOf(),
															se: regression.get('se'),
															predictor: covariate,
															mean: xMean,
															outcome: outcome.valueOf(),
															treatment: structuredQuery.interventions.valueOf()?.[0]?.label,
														})
													}
													localStorage.setItem('treatment-variance', treatmentVariance.valueOf().toString())
													save()
												},
											}),
										]),
									])
								]
							}),
						)
						// TO DO: put chart code into a method for catching errors 
						let yMinValues = yMin,
							yMaxValues = yMax,
							width = 500 - margin.left - margin.right,
							height = 280 - margin.top - margin.bottom
						new ElementRenderer({
							element: chartDiv,
							variable: all([
								slope,
								xMinVar,
								xMaxVar,
								useRegularization,
								treatmentVariance,
								regressionDiagnostics,
							]),
							renderUpdate: async ([
								slope,
								xMin,
								xMax,
								useRegularization,
								treatmentVariance,
								regressionDiagnostics,
							]) => {
								chartDiv.innerHTML = ''
								if (!slope) {
									this.body.classList.add('no-regression-data')
									loadingClock.remove()
									needRegressionData(true)
									return
								}
								let { m, lb, ub, se } = slope
								this.body.classList.remove('no-regression-data')
								let b = bForM(m)
								let d3 = await import('d3')
								var svg = d3
									.select(chartDiv)
									.append('svg')
									.attr('width', 500)
									.attr('height', 280)
									.on("mouseover", function () { hoverCircle.style("display", null); })
									.on("mouseout", function () { hoverCircle.style("display", "none"); })
									.on('mousemove', function (nodeData, event) {
										let x = d3.event.offsetX - 60
										let v = reverseScaleX(x)
										liveCovariate.put(v)
										let effect = v * m + b
										if (isRatio) effect = Math.exp(effect)
										liveEffect.put(effect)
										verticalHoverPath[0].x = x
										verticalHoverPath[0].y = yAxis(effect)
										verticalHoverPath[1].x = x
										horizontalHoverPath[0].y = yAxis(effect)
										horizontalHoverPath[1].y = yAxis(effect)
										horizontalHoverPath[1].x = x
										updateHover()
									})
								let mainGroup = svg
									.append('g')
									.attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')

								var xAxis = d3.scaleLinear().domain([xMin, xMax]).range([0, width])
								let hoverGroup = mainGroup.append('g')
								mainGroup
									.append('g')
									.attr('transform', 'translate(0,' + height + ')')
									.call(d3.axisBottom(xAxis))

								let xStartPosition = xAxis(xMin)
								let xScale = (xMax - xMin) / (xAxis(xMax) - xAxis(xMin))
								let reverseScaleX = (x) => (x - xStartPosition) * xScale + xMin

								let adjustScale = isRatio ? Math.exp : (v) => v
								yMin = Math.min(yMinValues, adjustScale(xMin * m + b), adjustScale(xMax * m + b))
								yMax = Math.max(yMaxValues, adjustScale(xMin * m + b), adjustScale(xMax * m + b))
								// Add Y axis
								var yAxis = d3
									.scaleLinear()
									.domain([yMin - yRange / 4, yMax + yRange / 4])
									.range([height, 0])
								mainGroup.append('g').call(d3.axisLeft(yAxis))

								let yStartPosition = yAxis(yMin)
								let yScale = (yMax - yMin) / (yAxis(yMax) - yAxis(yMin))
								let reverseScaleY = (y) => (y - yStartPosition) * yScale

								// Add dots
								mainGroup
									.append('g')
									.selectAll('dot')
									.data(data)
									.enter()
									.append('circle')
									.attr('cx', function (d) {
										return xAxis(d.x)
									})
									.attr('cy', function (d) {
										return yAxis(d.y)
									})
									.attr('r', function (d) {
										return Math.sqrt(Math.sqrt(d.N)) / 2 + 1
									})
									.style('fill', '#69b3a2')
									// TODO: Probably can remove this if we really don't need any hover effect on study circles
									/*.on('mouseover', function (nodeData, event) {
										svg.select('#scatter-study-' + nodeData.id).classed('hovered', true)
									})
									.on('mouseout', function (nodeData, event) {
										svg.select('#scatter-study-' + nodeData.id).classed('hovered', false)
									})*/
									.append('title').text(d => d.name)

								function xPosition(d) {
									let posX = xAxis(d.x)
									let estNameWidth = d.name.length * 6.75
									if (posX + estNameWidth > width)
										return posX - estNameWidth - 6
									else
										return posX + 6
								}
								mainGroup
									.append('g')
									.selectAll('text')
									.data(data)
									.enter()
									.append('svg:text')
									.attr('x', xPosition)
									.attr('y', function (d) {
										return yAxis(d.y)
									})
									.attr('class', 'scatter-study-label')
									.classed('visible', function (d) {
										return data.every((other) => {
											return xPosition(d) + d.name.length * 6.75 < xPosition(other) ||
												xPosition(d) > xPosition(other) + other.name.length * 4 ||
												Math.abs(yAxis(other.y) - yAxis(d.y)) > 12 ||
												d == other
										})
									})
									.attr('id', function (d) {
										return 'scatter-study-' + d.id
									})
									//.attr('class', (data) => (data.disabled ? 'disabled' : ''))
									.text((data) => data.name)

								let { lowerBound, upperBound } = regressionDiagnostics
								let mainSE = (upperBound - lowerBound) / 3.92
								let path = [],
									lbPath = [],
									ubPath = []
								let increment = (xMax - xMin) / 100
								path = []
								if (increment > 0) {
									for (let x = xMin; x <= xMax; x += increment) {
										let y = b + m * x
										path.push({ x, y: scaleIntoY(y) })
										let covariateSE = (xMean - x) * se
										let totalSE = Math.sqrt(mainSE * mainSE + covariateSE * covariateSE)
										let ciSize = totalSE * 1.92
										lbPath.push({ x, y: scaleIntoY(y - ciSize) })
										ubPath.push({ x, y: scaleIntoY(y + ciSize) })
									}
								}
								function scaleIntoY(v: number) {
									v = isRatio ? Math.exp(v) : v
									return Math.min(Math.max(-10, v), 1000)
								}
								assignPath(mainGroup.append('path'), path, '#300', 1.5)
								assignPath(mainGroup.append('path'), lbPath, '#aaf', 1, true)
								assignPath(mainGroup.append('path'), ubPath, '#aaf', 1, true)
								function assignPath(svg, path, color, width, dash?) {
									let svgPath = svg
										.datum(path)
										.attr('fill', 'none')
										.attr('stroke', color)
										.attr('stroke-width', width)
										.attr(
											'd',
											d3
												.line()
												.x(function (d) {
													return xAxis(d.x)
												})
												.y(function (d) {
													return yAxis(d.y)
												})
										)
									if (dash) svgPath.attr('stroke-dasharray', 4)
								}
								let horizontalHoverPath = [
									{ x: 0, y: 0 },
									{ x: 400, y: 0 },
								]
								let verticalHoverPath = [
									{ x: 100, y: 0 },
									{ x: 100, y: 360 },
								]
								let horizontalHover = hoverGroup.append('path')
								let verticalHover = hoverGroup.append('path')
								let hoverCircle = hoverGroup.append('circle')

								function updateHover() {
									verticalHover
										.datum(verticalHoverPath)
										.attr('fill', 'none')
										.attr('stroke', '#ccc')
										.attr('stroke-width', 1)
										.attr(
											'd',
											d3
												.line()
												.x((d) => d.x)
												.y((d) => d.y)
										)
									horizontalHover
										.datum(horizontalHoverPath)
										.attr('fill', 'none')
										.attr('stroke', '#ccc')
										.attr('stroke-width', 1)
										.attr(
											'd',
											d3
												.line()
												.x((d) => d.x)
												.y((d) => d.y)
										)
									hoverCircle
										.attr('stroke', '#333')
										.attr('stroke-width', 1)
										.attr('cx', horizontalHoverPath[1].x)
										.attr('cy', horizontalHoverPath[1].y)
										.attr('r', 4)
										.attr('fill', 'none')
								}

								const zoomHandler = d3.zoom().on('zoom', (event) => {
									let transform = d3.event.transform
									transform.k = Math.max(transform.k, 0.1)
									svg.attr('transform', transform)
								})
								zoomHandler(svg)
								loadingClock.remove()
								const regressionElement = document.querySelector('.regression-container')
								regressionElement.classList.remove('loading')
							},
						})
					}
				})
			}
		} else {
			// Regression analysis unavailable - handling demographic information
			let raceDialog = this.data?.xName == 'race'
			this.body.append(
				Div(`.regression-container.alternate-view${raceDialog ? '.race-content' : ''}`, [
					Div('.regression-dialog-left-pane', [
						// Until we support regression for race, we want to remove regression content
						!raceDialog ? (
						Div('.regression-summary', [
							H2('.strong-label', ['Regression Analysis is not applicable']),
							P(['Your current search does not have a clear direct comparison and so regression analysis is not possible.']),
							P([Span('.strong', ['Note: ']), 'To be able to run a regression analysis try limiting your search to one treatment.'])
						])) : null,
						Div('.characteristic-analysis', [
							H2('.strong-label', ['Population Analysis']),
							...(this.characteristicAnalysis || []),
						])
					]),
					Div('.regression-dialog-right-pane', [
						Div('.settings-container', [
							H2('.strong-label', ['Settings']),
							...(this.characteristicSetting || []),
						]),
						Div('.save', [
							Button('.primary', {
								content: 'Save',
								onclick: () => save (),
							}),
						]),
					])
				])
			)
		}
		const save = () => {
			this.onSave()
			this.hide()
		}
	}
}

